<template>
    <div class="flex flex-col dark:text-white">
        <div class="flex flex-col my-5">
            <form class="flex items-center content-center" @submit.prevent="users = []; next_page_url = false; fetch();">
                <div class="my-4 mr-2 w-[250px] flex-none">
                    <date-range-picker
                        v-model="filters.date"
                        :formatter="{
                            date: 'DD/MM/YYYY',
                            month: 'MMM'
                        }"
                        :shortcuts="false"
                    ></date-range-picker>
                </div>
                <div class="grow">
                    <input v-model="filters.text" type="text" id="text" class="text-input" placeholder="Filtre por nome ou email">
                </div>
                <div class="flex flex-none ml-auto">
                    <button
                        @click.prevent="clearFilters"
                        :disabled="false"
                        type="button"
                        :class="{'cursor-not-allowed': loading}"
                        class="btn-outline-primary-sm mx-2 py-1.5!"
                    >
                        Limpar filtros
                    </button>
                    <button
                        :disabled="false"
                        type="submit"
                        :class="{'cursor-not-allowed': loading}"
                        class="inline-flex items-center rounded-md bg-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-xs transition hover:bg-indigo-400"
                    >
                        <svg v-if="loading" class="w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Filtrar
                    </button>
                </div>
            </form>
        </div>
        <ticto-loading v-if="loading && users.length === 0" />
        <empty-state v-else-if="users.length === 0" />
        <div
            v-else
            class="not-prose relative overflow-hidden rounded-xl border border-black/5 bg-white shadow-xs dark:border-white/10 dark:bg-white/[0.02]"
        >
            <ul
                role="list"
                class="divide-y divide-black/5 dark:divide-white/[0.06]"
            >
                <li v-for="user in users" :key="user.id">
                    <div
                        class="flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
                    >
                        <div class="shrink-0">
                            <dice-bear-avatar :seed="user.name" />
                        </div>

                        <a
                            @click.prevent="manage(user)"
                            class="min-w-0 flex-1 hover:cursor-pointer"
                        >
                            <div class="flex flex-wrap items-center gap-2">
                                <p
                                    class="truncate text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ user.name }}
                                </p>
                                <span :class="kycStatusChip(user.status)">
                                    {{ statusLabel(user.status) }}
                                </span>
                                <span
                                    v-if="user.idwall_status"
                                    :class="idwallStatusBadgeClass(user.idwall_status)"
                                    class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                >
                                    IDWall:
                                    {{ idwallStatusLabel(user.idwall_status) }}
                                </span>
                            </div>
                            <p
                                class="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ user.email }} ·
                                {{ user.type === "company" ? user.cnpj : user.cpf }}
                                <template v-if="translateReasons(user.reasons)">
                                    · {{ translateReasons(user.reasons) }}
                                </template>
                            </p>
                        </a>

                        <div
                            class="hidden shrink-0 flex-col items-end gap-0.5 md:flex"
                        >
                            <span
                                class="text-sm font-medium tabular-nums text-gray-900 dark:text-white"
                            >
                                Score {{ user.score ?? "–" }}
                            </span>
                            <time
                                v-if="user.last_checked_at"
                                :datetime="user.last_checked_at"
                                class="text-xs tabular-nums text-gray-400 dark:text-gray-500"
                            >
                                {{ user.last_checked_at }}
                            </time>
                            <span
                                v-else
                                class="text-xs text-gray-400 dark:text-gray-500"
                            >
                                Não solicitado
                            </span>
                        </div>

                        <a
                            v-if="user.last_kyc_process_external_id || user.status"
                            :href="getExternalKycUrl(user.last_kyc_process_external_id, user.type, user.cnpj)"
                            target="_blank"
                            class="chip-indigo shrink-0 hover:bg-indigo-500/20"
                        >
                            KYC externo
                        </a>

                        <ChevronRightIcon
                            class="h-4 w-4 shrink-0 text-gray-300 dark:text-gray-600"
                            aria-hidden="true"
                        />
                    </div>
                </li>
            </ul>
        </div>

        <div class="my-5 flex w-full" v-if="users.length > 0">
            <div class="mx-auto flex flex-col items-center">
                <span class="mb-1 text-xs tabular-nums text-gray-400">
                    Exibindo {{ pagination.from }} a {{ pagination.to }} de
                    {{ pagination.total }}
                </span>
                <pagination
                    :pagination="pagination"
                    :offset="1"
                    @paginate="fetch"
                ></pagination>
            </div>
        </div>
    </div>
</template>

<script>
import { ChevronRightIcon } from "@heroicons/vue/24/outline";
import DiceBearAvatar from "../../components/common/DiceBearAvatar.vue";
import EmptyState from "../../components/layouts/EmptyState.vue";
import TictoLoading from "../../components/layouts/TictoLoading.vue";
import VueSelect from "../../components/forms/VueSelect.vue";
import Pagination from "../../components/layouts/Pagination.vue";
import { mapWritableState, mapActions } from 'pinia';
import { Store } from '../../stores/store.js';
import HelperMixin from "../../mixins/HelperMixin.vue";

export default {
    components: { VueSelect, Pagination, ChevronRightIcon, DiceBearAvatar, EmptyState, TictoLoading },
    mixins: [HelperMixin],
    data() {
        return {
            loading: false,
            next_page_url: false,
            users: [],
            pagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 11,
                from: 1,
                to: 11
            },
            filters: {
                text: '',
                date: [],
            },
            aegisUrl: '',
        };
    },

    computed: {
        ...mapWritableState(Store, ['user']),
    },

    mounted() {
        this.storeProduct = false;
        this.loadConfig();
        this.fetch();
    },

    methods: {
        loadConfig() {
            axios.get('/api/kyc/config')
                .then(({ data }) => {
                    this.aegisUrl = data.aegis_url?.replace('api/v1', '') || '';
                })
                .catch(error => {
                    console.error('Error loading KYC config:', error);
                });
        },
        clearFilters() {
            this.users = [];
            this.filters.status = [];
            this.filters.text = '';
            this.filters.date = [];
            this.next_page_url = false;
            this.fetch();
        },
        translateReasons(reasons) {
            if (!reasons) {
                return null;
            }

            reasons = JSON.parse(reasons);

            let reasonTranslations = {
                'first_sell': 'Primeira venda',
                'without_valid_documents': 'Não possui documentos validos',
                'score_below_fifty': 'Pontos abaixo de 50',
                'total_revenue_amount': 'Faixa de faturamento',
            };

            return reasons.map(reason => reasonTranslations[reason] ?? reason).join(', ');
        },
        getExternalKycUrl(externalId, userType, cnpj) {
            if (userType === 'company' && cnpj) {
                return `${this.aegisUrl}company-report/${cnpj}/kyc-provider-page`;
            }

            return `${this.aegisUrl}kyc-process/${externalId}/customer-kyc-provider-page`;
        },
        manage(user) {
            this.user = user;
            this.$router.push({
                name: 'user.commissions',
                params: { user_id: user.id }
            });
        },
        statusLabel(status) {
            return {
                'checked': 'Verificado',
                'required': 'Requerido',
                'processing': 'Em processamento',
                'inconclusive': 'Inconcluso',
            }?.[status] ?? 'Não solicitado';
        },
        kycStatusChip(status) {
            return {
                'checked': 'chip-emerald',
                'required': 'chip-amber',
                'processing': 'chip-sky',
                'inconclusive': 'chip-orange',
            }?.[status] ?? 'chip-slate';
        },
        idwallStatusLabel(status) {
            return {
                'approved':                'Aprovado',
                'finished':                'Aprovado',
                'pending':                 'Pendente',
                'in_progress':             'Em análise',
                'waiting_manual_approval': 'Aguardando revisão manual',
                'waiting_manual_action':   'Aguardando ação manual',
                'inconclusive':            'Inconclusivo',
                'rejected':                'Rejeitado',
                'cancelled':               'Cancelado',
                'invalid':                 'Inválido',
            }?.[status] ?? status;
        },
        idwallStatusBadgeClass(status) {
            return {
                'approved':                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                'finished':                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                'pending':                 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                'in_progress':             'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                'waiting_manual_approval': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                'waiting_manual_action':   'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                'inconclusive':            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                'rejected':                'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                'cancelled':               'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
                'invalid':                 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
            }?.[status] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
        },
        fetchMissingIdwallStatuses(users) {
            const missing = users.filter(u => !u.idwall_status);

            missing.forEach(user => {
                axios.get(`/api/kyc/users/${user.id}/idwall-status`)
                    .then(({ data }) => {
                        if (data.idwall_status) {
                            const index = this.users.findIndex(u => u.id === user.id);
                            if (index !== -1) {
                                this.users[index] = { ...this.users[index], idwall_status: data.idwall_status };
                                this.users = [...this.users];
                            }
                        }
                    })
                    .catch(() => {});
            });
        },
        fetch() {
            this.loading = true;

            const params = {};
            params['page'] = this.pagination.current_page;
            params['filter[text]'] = this.filters.text;
            params['filter[betweenDates]'] = this.filters.date.join();

            axios.get('/api/kyc/users', {
                params: params
            }).then(({data}) => {
                this.users = data.data;

                this.pagination = {
                    ...this.pagination,
                    ...data
                };

                this.next_page_url = data.next_page_url;
                this.loading = false;

                this.fetchMissingIdwallStatuses(this.users);
            }).catch(error => {
                this.loading = false;
                // this.$toast.error(error.response.data.message);
            });
        },
    }
}
</script>
